import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  QrCode,
  Home,
  Building2,
  CreditCard,
  Settings,
  Menu,
  X,
  LogOut,
  User,
  FileText,
  Megaphone,
  Users,
  Package,
  ShoppingBag,
  Radio,
  Map,
  Ticket,
  ShieldCheck,
  PhoneCall,
  MessageSquare,
  Gift,
  Receipt,
  ScanLine,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, canManage, isSales, isMarketingManager } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = isSales
    ? [
        { name: 'Dashboard', href: '/sales-dashboard', icon: Home },
        { name: 'Leads', href: '/leads', icon: PhoneCall },
        { name: 'Companies', href: '/companies', icon: Building2 },
        { name: 'Company Map', href: '/company-map', icon: Map },
        { name: 'Marketing Channels', href: '/marketing-channels', icon: Radio },
      ]
    : isMarketingManager
    ? [
        { name: 'Dashboard', href: '/marketing-dashboard', icon: Home },
        { name: 'Leads', href: '/leads', icon: PhoneCall },
        { name: 'Company Map', href: '/company-map', icon: Map },
        { name: 'Marketing Channels', href: '/marketing-channels', icon: Radio },
        { name: 'Subscription Invoices', href: '/subscription-invoices', icon: FileText },
      ]
    : [
        { name: 'Dashboard', href: '/', icon: Home },
        { name: 'Companies', href: '/companies', icon: Building2 },
        { name: 'Leads', href: '/leads', icon: PhoneCall },
        { name: 'Subscriptions', href: '/subscriptions', icon: CreditCard },
        { name: 'Subscription Invoices', href: '/subscription-invoices', icon: FileText },
        { name: 'Marketing Programs', href: '/marketing-programs', icon: Megaphone },
        { name: 'Marketing Channels', href: '/marketing-channels', icon: Radio },
        { name: 'Company Map', href: '/company-map', icon: Map },
        { name: 'Affiliate', href: '/affiliate', icon: Users },
        { name: 'Store Items', href: '/store-items', icon: Package },
        { name: 'Store Invoices', href: '/store-invoices', icon: ShoppingBag },
        { name: 'Subscription Vouchers', href: '/subscription-vouchers', icon: Ticket },
        { name: 'Bundling', href: '/bundles', icon: Gift },
        { name: 'Pembelian Manual', href: '/manual-purchases', icon: Receipt },
        { name: 'Pendaftaran QRIS', href: '/qris-registrations', icon: QrCode },
        { name: 'Import Foto Menu', href: '/menu-imports', icon: ScanLine },
        { name: 'Review Feedback', href: '/review-feedbacks', icon: MessageSquare },
        ...(canManage ? [{ name: 'Kelola Admin', href: '/admin-users', icon: ShieldCheck }] : []),
        { name: 'Settings', href: '/settings', icon: Settings },
      ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white">
          <div className="flex h-16 items-center justify-between px-4">
            <h1 className="text-xl font-bold text-gray-900">Kloa Admin</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
          <div className="flex items-center h-16 px-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900">Kloa Admin</h1>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                    isActive
                      ? 'bg-primary-100 text-primary-900'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <item.icon className="mr-3 h-5 w-5" />
                  {item.name}
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1" />
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              <div className="flex items-center gap-x-2">
                <User className="h-5 w-5 text-gray-400" />
                <span className="text-sm font-medium text-gray-700">{user.fullName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-x-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <LogOut className="h-5 w-5" />
                Logout
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
